import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from '@/components/blocks/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, Book, Rocket, Zap, Users, HeadphonesIcon, Menu, X, List, DollarSign, UserCheck, Brain, FileText, Copy, Plus, Palette, Layers, Folder, Mail, Phone, MessageCircle } from 'lucide-react';
import { EnhancedCopyButton } from '@/components/ui/enhanced-copy-button';
import { HeroVideoDialog } from '@/components/ui/hero-video-dialog';
import videoThumb from '@/assets/quickatoo-video-thumbnail.png';
import FloatingActionMenu from '@/components/ui/floating-action-menu';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Seo } from '@/components/Seo';

const SITE_URL = 'https://quickatoo.com';

const LEARN_VIDEOS: Array<{ name: string; description: string; id: string }> = [
  { name: 'What is Quickatoo?', description: 'An introduction to Quickatoo, the quoting and job management app for painting contractors.', id: 'tAPzq2VOvOM' },
  { name: 'Quickatoo Onboarding Guide', description: 'Step-by-step walkthrough of setting up your Quickatoo account.', id: '_dA5bkN7gbI' },
  { name: 'Quote Template Setup', description: 'How to configure your painting quote template in Quickatoo.', id: '3Y_wh4tygJI' },
  { name: 'Clients Management', description: 'How to add, organise and manage your painting clients in Quickatoo.', id: 'D07ieizYeu4' },
  { name: 'Substrate Management', description: 'How to set up and manage substrates for accurate painting estimates.', id: 'DJMwH7CXYtU' },
  { name: 'Quote Management', description: 'How to track, send and manage painting quotes in Quickatoo.', id: 'd4QBOP2mkX0' },
  { name: 'Creating Your Quote in Quickatoo', description: 'Build an accurate painting quote from start to finish in minutes.', id: 'OXSgXU_kFkY' },
  { name: 'Add Your Terms & Conditions', description: 'How to attach your terms and conditions to Quickatoo quotes.', id: 'yFcv9u13Ux4' },
];

const learnJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Learn Quickatoo — Guides for Painting Contractors',
    description:
      'Guides, tutorials and FAQs covering quoting, substrates, clients, templates and job management in Quickatoo.',
    url: `${SITE_URL}/learn`,
    inLanguage: 'en',
    author: { '@type': 'Organization', name: 'Quickatoo', url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'Quickatoo',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/apple-touch-icon.png` },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Learn', item: `${SITE_URL}/learn` },
    ],
  },
  ...LEARN_VIDEOS.map((v) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: v.name,
    description: v.description,
    thumbnailUrl: `https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`,
    embedUrl: `https://www.youtube.com/embed/${v.id}`,
    contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
    uploadDate: '2025-01-01',
    publisher: { '@type': 'Organization', name: 'Quickatoo', url: SITE_URL },
  })),
];

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const DocumentationSidebar = ({ 
  activeSection, 
  onSectionChange, 
  isOpen, 
  onClose 
}: { 
  activeSection: string; 
  onSectionChange: (section: string) => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const sections = [
    {
      title: "Getting Started",
      items: [
        { id: "introduction", title: "Introduction", icon: Book },
        { id: "quick-start", title: "Quick Start", icon: Rocket }
      ]
    },
      {
        title: "Features",
        items: [
          { id: "quote-customization", title: "Quote Customization", icon: Palette },
          { 
            id: "terms-conditions", 
            title: "Terms & Conditions", 
            icon: FileText,
            subItems: [
              { id: "terms-conditions-templates", title: "T&C Templates", icon: FileText }
            ]
          },
          { id: "clients-management", title: "Clients Management", icon: Users },
          { id: "substrate-management", title: "Substrate Management", icon: Layers },
          { 
            id: "quote-management", 
            title: "Quote Management", 
            icon: Folder,
            subItems: [
              { id: "create-a-quote", title: "Create a Quote", icon: Plus }
            ]
          }
        ]
      },
    {
      title: "Support",
      items: [
        { id: "faq", title: "FAQ", icon: HeadphonesIcon },
        { id: "contact", title: "Contact Support", icon: Users }
      ]
    }
  ];

  const handleSectionClick = (sectionId: string) => {
    onSectionChange(sectionId);
    onClose(); // Close mobile sidebar after selection
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto
        ${isOpen ? 'fixed top-16 left-0 bottom-0 z-50' : 'hidden'}
        md:block md:sticky md:top-16 md:h-[calc(100vh-4rem)]
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6 md:justify-start">
            <h2 className="text-lg font-semibold text-gray-900">Documentation</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="md:hidden"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          {sections.map((section) => (
            <div key={section.title} className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleSectionClick(item.id)}
                        className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                          activeSection === item.id
                            ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {typeof item.icon === 'string' ? (
                          <span className="w-4 h-4 mr-3 flex items-center justify-center text-xs grayscale">{item.icon}</span>
                        ) : (
                          <Icon className="w-4 h-4 mr-3" />
                        )}
                        {item.title}
                      </button>
                      {/* Render sub-items if they exist */}
                      {item.subItems && (
                        <ul className="ml-7 mt-2 space-y-1 border-l-2 border-gray-200 pl-4">
                          {item.subItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <li key={subItem.id}>
                                <button
                                  onClick={() => handleSectionClick(subItem.id)}
                                  className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors relative ${
                                    activeSection === subItem.id
                                      ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                >
                                  <div className="absolute -left-6 w-2 h-2 bg-gray-300 rounded-full"></div>
                                  <SubIcon className="w-3 h-3 mr-3 opacity-75" />
                                  <span className="text-xs font-medium">{subItem.title}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const TableOfContents = ({ activeSection }: { activeSection: string }) => {
  const getToC = (section: string) => {
    switch (section) {
      case "introduction":
        return [
          { id: "what-is-paintapp", title: "What is Quickatoo?" },
          { id: "key-features", title: "Key Features" },
          { id: "who-its-for", title: "Who It's For" }
        ];
      case "quick-start":
        return [
          { id: "confirm-email", title: "Confirm Your Email" },
          { id: "complete-profile", title: "Complete Your Profile" },
          { id: "company-details", title: "Company Details Setup" },
          { id: "upload-logo", title: "Upload Logo & Set Rate" },
          { id: "add-address", title: "Add Address & Business Info" },
          { id: "start-project", title: "Start Your First Project" }
        ];
      case "quote-customization":
        return [
          { id: "open-quote-settings", title: "Open Quote Settings" },
          { id: "choose-cover-style", title: "Choose a Cover Style" },
          { id: "pick-brand-colour", title: "Pick a Brand Colour" },
          { id: "set-expiry-date", title: "Set Expiry Date" },
          { id: "add-signature", title: "Add Your Signature" },
          { id: "set-rates", title: "Set Your Rates" },
          { id: "review-terms", title: "Review Terms & Conditions" },
          { id: "save-changes", title: "Save Your Changes" }
        ];
      case "clients-management":
        return [
          { id: "open-clients-page", title: "Open the Clients Page" },
          { id: "choose-client-type", title: "Choose Client Type" },
          { id: "fill-personal-details", title: "Fill in Personal Details" },
          { id: "add-contact-info", title: "Add Contact Info" },
          { id: "set-address-notes", title: "Set Address & Notes" },
          { id: "click-create-client", title: "Click Create Client" }
        ];
      case "substrate-management":
        return [
          { id: "open-surface-settings", title: "Open Surface Settings" },
          { id: "use-edit-preset-surfaces", title: "Use or Edit Preset Surfaces" },
          { id: "add-new-surface", title: "Add a New Surface" },
          { id: "save-edit-anytime", title: "Save or Edit Anytime" }
        ];
      case "quote-management":
        return [
          { id: "access-all-quotes", title: "Access All Quotes" },
          { id: "quote-options-menu", title: "Quote Options Menu" },
          { id: "view-edit-quote", title: "View & Edit a Quote" },
          { id: "add-project-photos", title: "Add Project Photos" },
          { id: "summary-reports", title: "Summary & Reports" },
          { id: "apply-discounts", title: "Apply Discounts" },
          { id: "generate-quote-pdf", title: "Generate a Quote PDF" },
          { id: "detailed-quote-report", title: "Detailed Quote Report" },
          { id: "client-view-acceptance", title: "Client View & Acceptance" }
        ];
      case "create-a-quote":
        return [
          { id: "start-new-quote", title: "Start a New Quote" },
          { id: "fill-quote-details", title: "Fill Quote Details" },
          { id: "add-rooms", title: "Add Rooms" },
          { id: "add-doors-windows", title: "Add Doors & Windows" },
          { id: "measurement-modes", title: "Measurement Modes" },
          { id: "exterior-quote", title: "Exterior Quote" },
          { id: "review-finalise", title: "Review & Finalise" },
          { id: "save-send", title: "Save & Send" }
        ];
      case "terms-conditions":
        return [
          { id: "go-quote-settings", title: "Go to Quote Settings" },
          { id: "add-new-term", title: "Add a New Term" },
          { id: "example-inclusions", title: "Example: Add Inclusions" },
          { id: "format-text", title: "Format Your Text" },
          { id: "confirm-saved", title: "Confirm It's Saved" },
          { id: "add-spec-optional", title: "Add a Spec (Optional)" },
          { id: "make-default", title: "Make Them Default" },
          { id: "done", title: "Done!" }
        ];
      case "terms-conditions-templates":
        return [
          { id: "template-overview", title: "Template Overview" },
          { id: "inclusions-templates", title: "Inclusions Templates" },
          { id: "exclusions-templates", title: "Exclusions Templates" },
          { id: "specifications-templates", title: "Specifications Templates" },
          { id: "paint-application-templates", title: "Paint Application Templates" },
          { id: "get-help", title: "Need Custom Help?" }
        ];
      default:
        return [];
    }
  };

  const tocItems = getToC(activeSection);

  if (tocItems.length === 0) return null;

  return (
    <div className="w-64 h-[calc(100vh-4rem)] sticky top-16 bg-white border-l border-gray-200 overflow-y-auto hidden xl:block">
      <div className="p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
          On This Page
        </h3>
        <ul className="space-y-2">
          {tocItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => {
                  const element = document.getElementById(item.id);
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-sm text-gray-600 hover:text-gray-900 block py-1 text-left w-full"
              >
                {item.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const FloatingTableOfContents = ({ activeSection }: { activeSection: string }) => {
  const getToC = (section: string) => {
    switch (section) {
      case "introduction":
        return [
          { id: "what-is-paintapp", title: "What is Quickatoo?" },
          { id: "key-features", title: "Key Features" },
          { id: "who-its-for", title: "Who It's For" }
        ];
      case "quick-start":
        return [
          { id: "confirm-email", title: "Confirm Your Email" },
          { id: "complete-profile", title: "Complete Your Profile" },
          { id: "company-details", title: "Company Details Setup" },
          { id: "upload-logo", title: "Upload Logo & Set Rate" },
          { id: "add-address", title: "Add Address & Business Info" },
          { id: "start-project", title: "Start Your First Project" }
        ];
      case "quote-customization":
        return [
          { id: "open-quote-settings", title: "Open Quote Settings" },
          { id: "choose-cover-style", title: "Choose a Cover Style" },
          { id: "pick-brand-colour", title: "Pick a Brand Colour" },
          { id: "set-expiry-date", title: "Set Expiry Date" },
          { id: "add-signature", title: "Add Your Signature" },
          { id: "set-rates", title: "Set Your Rates" },
          { id: "review-terms", title: "Review Terms & Conditions" },
          { id: "save-changes", title: "Save Your Changes" }
        ];
      case "clients-management":
        return [
          { id: "open-clients-page", title: "Open the Clients Page" },
          { id: "choose-client-type", title: "Choose Client Type" },
          { id: "fill-personal-details", title: "Fill in Personal Details" },
          { id: "add-contact-info", title: "Add Contact Info" },
          { id: "set-address-notes", title: "Set Address & Notes" },
          { id: "click-create-client", title: "Click Create Client" }
        ];
      case "substrate-management":
        return [
          { id: "open-surface-settings", title: "Open Surface Settings" },
          { id: "use-edit-preset-surfaces", title: "Use or Edit Preset Surfaces" },
          { id: "add-new-surface", title: "Add a New Surface" },
          { id: "save-edit-anytime", title: "Save or Edit Anytime" }
        ];
      case "quote-management":
        return [
          { id: "access-all-quotes", title: "Access All Quotes" },
          { id: "quote-options-menu", title: "Quote Options Menu" },
          { id: "view-edit-quote", title: "View & Edit a Quote" },
          { id: "add-project-photos", title: "Add Project Photos" },
          { id: "summary-reports", title: "Summary & Reports" },
          { id: "apply-discounts", title: "Apply Discounts" },
          { id: "generate-quote-pdf", title: "Generate a Quote PDF" },
          { id: "detailed-quote-report", title: "Detailed Quote Report" },
          { id: "client-view-acceptance", title: "Client View & Acceptance" }
        ];
      case "create-a-quote":
        return [
          { id: "start-new-quote", title: "Start a New Quote" },
          { id: "fill-quote-details", title: "Fill Quote Details" },
          { id: "add-rooms", title: "Add Rooms" },
          { id: "add-doors-windows", title: "Add Doors & Windows" },
          { id: "measurement-modes", title: "Measurement Modes" },
          { id: "exterior-quote", title: "Exterior Quote" },
          { id: "review-finalise", title: "Review & Finalise" },
          { id: "save-send", title: "Save & Send" }
        ];
      case "terms-conditions":
        return [
          { id: "go-quote-settings", title: "Go to Quote Settings" },
          { id: "add-new-term", title: "Add a New Term" },
          { id: "example-inclusions", title: "Example: Add Inclusions" },
          { id: "format-text", title: "Format Your Text" },
          { id: "confirm-saved", title: "Confirm It's Saved" },
          { id: "add-spec-optional", title: "Add a Spec (Optional)" },
          { id: "make-default", title: "Make Them Default" },
          { id: "done", title: "Done!" }
        ];
      case "terms-conditions-templates":
        return [
          { id: "template-overview", title: "Template Overview" },
          { id: "inclusions-templates", title: "Inclusions Templates" },
          { id: "exclusions-templates", title: "Exclusions Templates" },
          { id: "specifications-templates", title: "Specifications Templates" },
          { id: "paint-application-templates", title: "Paint Application Templates" },
          { id: "get-help", title: "Need Custom Help?" }
        ];
      default:
        return [];
    }
  };

  const tocItems = getToC(activeSection);

  if (tocItems.length === 0) return null;

  const options = tocItems.map((item) => ({
    label: item.title,
    onClick: () => {
      const element = document.getElementById(item.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
  }));

  return (
    <div className="xl:hidden">
      <FloatingActionMenu
        options={options}
        triggerIcon={<List className="w-6 h-6" />}
        className="z-50"
      />
    </div>
  );
};

const ContentSection = ({ section, setActiveSection }: { section: string; setActiveSection: (section: string) => void }) => {
  const getSectionInfo = (section: string) => {
    switch (section) {
      case "introduction":
        return { title: "Introduction", category: "Getting Started", breadcrumbs: [{ label: "Getting Started", link: "#quick-start" }] };
      case "quick-start":
        return { title: "Quick Start", category: "Getting Started", breadcrumbs: [{ label: "Getting Started", link: "#introduction" }] };
      case "quote-customization":
        return { title: "Quote Customization", category: "Features", breadcrumbs: [{ label: "Features", link: "#quote-customization" }] };
      case "clients-management":
        return { title: "Clients Management", category: "Features", breadcrumbs: [{ label: "Features", link: "#clients-management" }] };
      case "substrate-management":
        return { title: "Substrate Management", category: "Features", breadcrumbs: [{ label: "Features", link: "#substrate-management" }] };
      case "quote-management":
        return { title: "Quote Management", category: "Features", breadcrumbs: [{ label: "Features", link: "#quote-management" }] };
      case "terms-conditions":
        return { title: "Terms & Conditions", category: "Features", breadcrumbs: [{ label: "Features", link: "#terms-conditions" }] };
      case "terms-conditions-templates":
        return { 
          title: "T&C Templates", 
          category: "Features", 
          breadcrumbs: [
            { label: "Features", link: "#quote-customization" },
            { label: "Terms & Conditions", link: "#terms-conditions" }
          ] 
        };
      case "create-a-quote":
        return { 
          title: "Create a Quote", 
          category: "Features", 
          breadcrumbs: [
            { label: "Features", link: "#quote-customization" },
            { label: "Quote Management", link: "#quote-management" }
          ] 
        };
      case "faq":
        return { title: "FAQ", category: "Support", breadcrumbs: [{ label: "Support", link: "#faq" }] };
      case "contact":
        return { title: "Contact Support", category: "Support", breadcrumbs: [{ label: "Support", link: "#contact" }] };
      default:
        return { title: "Documentation", category: "Home", breadcrumbs: [] };
    }
  };

  const sectionInfo = getSectionInfo(section);

  const renderContent = () => {
    switch (section) {
      case "introduction":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome to Quickatoo</h1>
              <p className="text-sm text-gray-500 mb-4">Formerly PaintAPP</p>
              <p className="text-xl text-gray-600 mb-8">
                The complete solution for painting contractors to streamline quoting, project management, and business operations.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/tAPzq2VOvOM"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Introduction Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="what-is-paintapp">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Quickatoo?</h2>
              <p className="text-gray-700 mb-4">
                Quickatoo is a comprehensive business management platform designed specifically for painting contractors. 
                It combines powerful quoting tools, advanced measuring capabilities, project management, team management, 
                CRM functionality, business financial tracking, and business insights to help you grow your painting business efficiently.
                With AI integration coming soon, Quickatoo will become even more powerful in automating and optimizing your workflow.
              </p>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                  <p className="text-purple-800">
                    <strong>💡 Pro Tip:</strong> Professional-looking quotes help you win more jobs compared to basic estimates - 
                    Quickatoo ensures every quote reflects your expertise and attention to detail.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div id="key-features">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Zap className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Smart Quoting</h3>
                    </div>
                    <p className="text-gray-600">
                      Generate accurate quotes in minutes with our intelligent pricing algorithms and material calculators.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Zap className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Advanced Features</h3>
                    </div>
                    <p className="text-gray-600">
                      Advanced measuring tools help you calculate surface areas and material requirements accurately.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Users className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Project Management</h3>
                    </div>
                    <p className="text-gray-600">
                      Track projects from quote to completion with built-in scheduling and progress monitoring.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <UserCheck className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Team Management</h3>
                    </div>
                    <p className="text-gray-600">
                      Manage your team efficiently with role-based access, task assignments, and performance tracking.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Users className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">CRM System</h3>
                    </div>
                    <p className="text-gray-600">
                      Manage customer relationships, track leads, and maintain detailed client communication history.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <DollarSign className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Financial Tracking</h3>
                    </div>
                    <p className="text-gray-600">
                      Monitor your business finances with expense tracking, profit analysis, and financial reporting.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Book className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">Business Insights</h3>
                    </div>
                    <p className="text-gray-600">
                      Comprehensive reporting and analytics to help you make data-driven business decisions.
                    </p>
                  </CardContent>
                </Card>
                <Card className="relative">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-3">
                      <Brain className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-lg font-semibold">AI Integration</h3>
                      <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full ml-2">Coming Soon</span>
                    </div>
                    <p className="text-gray-600">
                      AI-powered automation for quote optimization, project planning, and predictive analytics.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="who-its-for">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Who It's For</h2>
              <p className="text-gray-700 mb-4">
                Quickatoo is designed for painting contractors of all sizes:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-purple-600 mr-2" />
                  <strong>Solo contractors</strong>&nbsp;looking to professionalize their quoting process
                </li>
                <li className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-purple-600 mr-2" />
                  <strong>Small teams</strong>&nbsp;needing better project coordination
                </li>
                <li className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-purple-600 mr-2" />
                  <strong>Growing businesses</strong>&nbsp;requiring scalable management solutions
                </li>
                <li className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-purple-600 mr-2" />
                  <strong>Established companies</strong>&nbsp;wanting to optimize their operations
                </li>
              </ul>
            </div>
          </div>
        );

      case "quick-start":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Quickatoo Onboarding Guide</h1>
              <p className="text-xl text-gray-600 mb-8">
                Get started in minutes — follow these simple steps.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/_dA5bkN7gbI"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Onboarding Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="confirm-email">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Confirm Your Email</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  After signing up, you'll receive an email. Click the green "Confirm My Email" button to activate your account.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/c6d00d3b-3593-44c9-9801-af3c65927b40.png"
                      alt="Email confirmation screen"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="complete-profile">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Complete Your Profile</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Fill in your basic info:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>First Name</li>
                  <li>Last Name</li>
                  <li>Company Name</li>
                  <li>Company Phone</li>
                </ul>
                <p className="text-gray-700">Then click "Complete Setup."</p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/92f25809-3f71-4a91-a48c-2279b072d3f5.png"
                      alt="Profile completion form"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="company-details">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Open Company Details</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Once the setup is complete, click "Got It" and go to: <strong>Settings → Company Details</strong>
                </p>
                <p className="text-gray-700">This is where you personalise your business.</p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/ba50b589-d2fb-411d-b314-f09929be6fae.png"
                      alt="Dashboard with settings navigation"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="upload-logo">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Upload Your Logo & Set Business Rate</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Your Logo</h3>
                  <p className="text-gray-700 mb-4">
                    Click the logo section and upload your company branding. This will appear on your quotes.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Your Business Rate</h3>
                  <p className="text-gray-700 mb-4">
                    Enter your hourly rate (e.g., $90/hour). This rate is used to calculate quote prices.
                  </p>
                  <Card className="bg-purple-50 border-purple-200 mb-4">
                    <CardContent className="p-4">
                      <p className="text-purple-800">
                        <strong>💡 Need help?</strong> Contact our team for help.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div id="add-address">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Add Company Address & Business Info</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Address</h3>
                  <p className="text-gray-700 mb-4">
                    Type your business address exactly as it should appear on your quotes.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">ABN & Website</h3>
                  <p className="text-gray-700 mb-4">
                    Add your ABN and your business website URL. These will be shown on official documents and quotes.
                  </p>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/e581bd4f-06f1-4251-a0ae-2d48bdb712ff.png"
                      alt="Company address and business information form"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="start-project">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Save & You're Ready</h2>
              <div className="space-y-6">
                <p className="text-gray-700">
                  Click "Save Changes." A confirmation message will appear — you're now ready to start quoting!
                </p>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">🎉 Congratulations!</h3>
                    <p className="text-gray-700 mb-6">
                      Your Quickatoo account is now fully set up. You're ready to create professional quotes and manage your painting projects.
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                      onClick={() => window.open('https://app.quickatoo.com/quotes', '_blank')}
                    >
                      <Rocket className="w-5 h-5 mr-2" />
                      Start Your First Project
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "quote-customization":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Quickatoo Quote Template Setup</h1>
              <p className="text-xl text-gray-600 mb-8">
                Quickly set up your quote appearance and preferences in just a few steps.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/3Y_wh4tygJI"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Quote Customization Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="open-quote-settings">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Open Quote Settings</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  From the side menu: Go to <strong>Settings → Quote Settings</strong>
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/feab39ae-3da5-4792-b8c3-ebdb64ec5cfa.png"
                      alt="Quote Settings page overview"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="choose-cover-style">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Choose a Cover Style</h2>
              <div className="space-y-4">
                <p className="text-gray-700">You've got 3 options:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>Standard Template</strong> – Simple and clean</li>
                  <li><strong>Upload Your Own</strong> – Use your custom image</li>
                  <li><strong>Colour Only</strong> – Pick a solid colour that matches your brand</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/df895ac5-fe6e-4356-91eb-053ac3a35090.png"
                      alt="Cover template options showing Standard, Paint & Tiles, Painting Tools, Material Picks, Color Fabrics, Creative Paint, and Bold Strokes"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="pick-brand-colour">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Pick a Brand Colour</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Use the colour picker to match your company colours.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/f1fd87d7-83d6-4cf6-a6c2-06d682e5f13e.png"
                      alt="Brand colour picker interface with gradient selector and color palette"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="set-expiry-date">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Set Expiry Date</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Choose how long your quotes stay valid:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>1 Month</li>
                  <li>3 Months</li>
                  <li>6 Months</li>
                  <li>1 Year</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/aa432fe6-acff-4293-b6fd-75f76817d2f2.png"
                      alt="Quote expiry date options showing 1 Year selection"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-signature">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Add Your Signature</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Use the box to draw your digital signature. Click Save Signature when done.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/b177e097-0f2e-4aae-8278-ace30fbef285.png"
                      alt="Digital signature area with completed handwritten signature"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="set-rates">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Set Your Rates</h2>
              <div className="space-y-4">
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>GST:</strong> Set to 10%</li>
                  <li><strong>Home Warranty:</strong> Select your state (e.g. Queensland)</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/2dec9929-cc5d-4fce-ae0c-76c12809eb9a.png"
                      alt="GST Rate set to 10% and Home Warranty showing QLD selection"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="review-terms">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Review Terms & Conditions</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Select the standard Terms & Conditions that are commonly used on most of your quotes. Additional specific terms can be added to individual quotes - these selected items will be pre-loaded as defaults when creating new quotes.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Application</li>
                  <li>Client Responsibilities</li>
                  <li>License Info</li>
                  <li>and more...</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/7a976557-6b1d-49e3-855d-0b9beb9174f0.png"
                      alt="Terms and conditions list showing Application, Client Responsibilities, Colours, Environment, License, and Preparation"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="save-changes">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Save Your Changes</h2>
              <div className="space-y-6">
                <p className="text-gray-700">
                  Click Save at the bottom to lock it in.
                </p>
                
                <Card className="bg-purple-50 border-purple-200 mb-4">
                  <CardContent className="p-6">
                    <p className="text-purple-800">
                      <strong>💡 Pro Tip:</strong> Customized quote templates help your business stand out and look more professional to potential clients.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">🎉 That's it!</h3>
                    <p className="text-gray-700 mb-6">
                      Your custom quote template is ready to go. Every quote you create will now use your professional branding and settings.
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                      onClick={() => window.open('https://app.quickatoo.com/settings', '_blank')}
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Start Customizing Your Quotes
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "clients-management":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Clients Management</h1>
              <p className="text-xl text-gray-600 mb-8">
                Add your first client in under 2 minutes — it's quick and simple!
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/D07ieizYeu4"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Clients Management Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="open-clients-page">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Open the "Clients" Page</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  From the left-hand menu, click <strong>Clients</strong> to open the client dashboard.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/e4436a75-c84e-46ef-9f4b-2d7fa6c33207.png"
                      alt="Quickatoo Clients page with dashboard showing Total Clients, Recent Quote Clients, and New This Month statistics"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="choose-client-type">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Choose Client Type</h2>
              <div className="space-y-4">
                <p className="text-gray-700">You'll see two options:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>Individual</strong></li>
                  <li><strong>Business</strong></li>
                </ul>
                <p className="text-gray-700">
                  Select <strong>Individual</strong>, then tap the <strong>➕</strong> button in the bottom-right corner.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/646c638f-d64e-4f93-8ab7-c8ef3b0ce023.png"
                      alt="Clients page showing Individual and Business tabs with the plus button highlighted"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="fill-personal-details">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Fill in Personal Details</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Enter the client's:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>First Name</strong></li>
                  <li><strong>Last Name</strong></li>
                  <li><strong>Birthday</strong> (optional but great for sending special deals)</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/5a0d4a1f-fdf2-4eb3-be89-06964bd1bc74.png"
                      alt="Add Individual Client form with First Name and Last Name fields filled in"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-contact-info">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Add Contact Info</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Fill in their:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li><strong>Email</strong></li>
                  <li><strong>Phone Number</strong></li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/806e00d5-2062-49a7-8f9b-71a656a8fc0a.png"
                      alt="Add Individual Client form with Email and Phone Number fields filled in"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="set-address-notes">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Set Address & Notes</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Enter their address, and jot down any useful notes — like preferences or job history.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/5e46e133-d38e-4e06-a1d9-7d8bfb127ee7.png"
                      alt="Add Individual Client form with Address and Notes fields completed"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="click-create-client">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Click "Create Client"</h2>
              <div className="space-y-6">
                <p className="text-gray-700">
                  When everything's filled out, tap <strong>Create Client</strong>.
                </p>
                <p className="text-gray-700">
                  You'll get a success message confirming it's saved.
                </p>
                
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/58ce47d2-073f-4b5e-9f1a-50ba0e3d9d69.png"
                      alt="Success message showing client created successfully with the new client visible in the clients list"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ You're All Set!</h3>
                    <p className="text-gray-700 mb-6">
                      Your new client is now in the system and ready to receive quotes, messages, and more via Quickatoo.
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                      onClick={() => window.open('https://app.quickatoo.com/clients', '_blank')}
                    >
                      <Users className="w-5 h-5 mr-2" />
                      Manage Your Clients
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "substrate-management":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Substrate Management</h1>
              <p className="text-xl text-gray-600 mb-8">
                Customise, add, or update surfaces for accurate and fast quoting.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/DJMwH7CXYtU"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Substrate Management Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="open-surface-settings">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Open Surface Settings</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  From the side menu, go to <strong>Settings → Interior Paint</strong> or <strong>Exterior Paint</strong> to access surface configurations.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/d8958de2-c1ab-4ce6-a256-4f7fb6d9d916.png" 
                      alt="Surface settings page showing interior paint options"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="use-edit-preset-surfaces">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use or Edit Preset Surfaces</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Quickatoo includes default surfaces for:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">Interior Surfaces</h4>
                      <ul className="list-disc list-inside text-purple-700 space-y-1 text-sm">
                        <li>Walls, Ceilings</li>
                        <li>Skirting Boards, Cornices</li>
                        <li>Doors, Windows</li>
                        <li>Custom Square/Linear/Item</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">Exterior Surfaces</h4>
                      <ul className="list-disc list-inside text-purple-700 space-y-1 text-sm">
                        <li>Custom Area</li>
                        <li>Custom Linear</li>
                        <li>Custom items</li>
                        <li>Doors, Windows</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
                <p className="text-gray-700">
                  You can use them as-is or click on any surface name to edit values like coverage, cost, or prep time.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/6e60514e-442c-42f5-aafa-2562a1d6a85a.png" 
                      alt="Edit substrate dialog showing form fields for modifying surface details"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-new-surface">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Add a New Surface</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click the <strong>➕</strong> button in the bottom-right corner.
                </p>
                <p className="text-gray-700">Fill in the form with:</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li><strong>Name</strong> (See Pro tip)</li>
                    <li><strong>Performance</strong> – Estimated time to paint per square meter, linear meter, or per item</li>
                    <li><strong>Drum Size</strong> (usually the largest sold)</li>
                    <li><strong>Cost per Drum</strong> (with markup)</li>
                    <li><strong>Spread Rate</strong> (m² per litre)</li>
                    <li><strong>Prep Time</strong> – Approximate time needed for a quick light sand between coats</li>
                  </ul>
                </div>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-6">
                    <p className="text-purple-800">
                      <strong>💡 Pro Tip:</strong> To help the app automatically classify the substrate type, include one of the following keywords in the substrate name: wall, ceiling, cornice, skirting, door, or window. If none of these are used, the app will treat it as a custom substrate.
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/5ed5f76b-1098-430f-83bc-715ff0e48255.png" 
                      alt="Add New Substrate dialog showing empty form"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="save-edit-anytime">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Save or Edit Anytime</h2>
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Once done, click <strong>Create</strong>.
                  </p>
                  <p className="text-gray-700">
                    To make changes later, just click the surface name to reopen and update.
                  </p>
                  <p className="text-gray-700">
                    To delete, tap the red bin icon and confirm.
                  </p>
                </div>
                
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/7458a970-525b-4e18-bcbc-ffb78034fb45.png" 
                      alt="Add New Substrate dialog with filled form showing completed substrate details"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-blue-800 mb-2">📋 Industry Standards</h4>
                    <p className="text-blue-700">
                      If you have established rates, you can modify the preset surfaces accordingly. 
                      If not, the preset surfaces follow industry standards and can be easily managed by professional painters.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ You're Set!</h3>
                    <p className="text-gray-700 mb-6">
                      Your surfaces are now ready to use in quotes. Preset surfaces follow industry standards — but you're always in control to fine-tune them to your business.
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                      onClick={() => window.open('https://app.quickatoo.com/settings', '_blank')}
                    >
                      <Layers className="w-5 h-5 mr-2" />
                      Manage Your Surfaces
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "quote-management":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Quote Management</h1>
              <p className="text-xl text-gray-600 mb-8">
                Quickatoo Quote System Overview
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Everything you can do with your quotes — manage, edit, send, and track with ease.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/d4QBOP2mkX0"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Quote Management Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="access-all-quotes">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Access All Quotes</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Go to the <strong>Quotes</strong> page to see a full list with:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Quote number</li>
                    <li>Client name & address</li>
                    <li>Status & last viewed (👁️ icon appears when the client opens it)</li>
                  </ul>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/7fc5ef1c-f41a-4a9c-bb34-b725e48b45d1.png" 
                      alt="Quickatoo Quotes page showing list of quotes with status indicators"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="quote-options-menu">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Quote Options Menu</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click the <strong>three dots</strong> in the top-right of each quote to:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Disable reminders</li>
                    <li>Duplicate the quote</li>
                    <li>Delete the quote</li>
                  </ul>
                </div>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-6">
                    <p className="text-purple-800">
                      <strong>💡 Pro Tip:</strong> To stop weekly follow-up emails, click the reminder icon. Click again to re-enable it.
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/cef45d40-4e1b-451d-a033-ab4ba1dc69b7.png" 
                      alt="Quote options dropdown menu showing disable reminders, duplicate, and delete options"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="view-edit-quote">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. View & Edit a Quote</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click on any quote to open its dashboard, where you can:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>See job value, hours, client info, and notes</li>
                    <li>Add or delete notes like "client wants to start ASAP"</li>
                  </ul>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/e9b2f99b-2b47-409c-8f1e-40d762f20845.png" 
                      alt="Quote dashboard showing client details, quote summary, and navigation tabs"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-project-photos">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Add Project Photos</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Upload up to 15 photos</li>
                    <li>You can view or delete them as needed</li>
                    <li>If the quote has been revised, revisions are shown here too</li>
                  </ul>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/a8f1e1dc-f5de-4144-97e0-8be309ad2a9e.png" 
                      alt="Quote Photos tab showing project photos upload interface with sample images"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="summary-reports">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Summary & Reports</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click <strong>View Summary</strong> to:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Print or email the quote</li>
                    <li>Export a detailed report</li>
                    <li>Toggle client reminders</li>
                    <li>Add extra costs (interior/exterior)</li>
                  </ul>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/12c4a97d-6dba-4a64-9346-894fe2c33e07.png" 
                      alt="Quote summary page showing client reminders, time and materials breakdown"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="apply-discounts">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Apply Discounts</h2>
              <div className="space-y-4">
                <p className="text-gray-700">Add either:</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>A % discount (e.g. 10% for new clients)</li>
                    <li>A fixed discount (e.g. $50 off)</li>
                  </ul>
                </div>
                <p className="text-gray-700">
                  Name the discount and click <strong>Save</strong> — it shows instantly on the client's version.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/bfd9c391-34ea-4540-befc-a7c556753ebf.png" 
                      alt="Quote breakdown showing discount application with percentage and fixed amount options"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="generate-quote-pdf">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Generate a Quote PDF</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click <strong>Print</strong> to export a full quote PDF that includes:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Included/excluded areas</li>
                    <li>Paint specs & surface details</li>
                    <li>Terms & conditions</li>
                    <li>Photos & pricing</li>
                    <li>Client signature field</li>
                  </ul>
                </div>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/94910f4e-eb4b-4ccf-90d2-ddd8c0326928.png" 
                      alt="Quote overview page with print and export options visible in the toolbar"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="detailed-quote-report">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Detailed Quote Report</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Open the quote report for:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>Material costs</li>
                    <li>Room-by-room surface breakdown</li>
                    <li>Hours for walls, ceilings, etc.</li>
                  </ul>
                </div>
                <p className="text-gray-700">
                  Great for tracking and managing projects precisely.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/a9ad7acb-b344-4d94-8dc2-2de68ad4bd46.png" 
                      alt="Detailed quote report showing project overview with interior and exterior breakdown"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="client-view-acceptance">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Client View & Acceptance</h2>
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="text-gray-700">
                    Click the <strong>"i"</strong> icon to preview what the client sees:
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
                    <ul className="list-disc list-inside text-blue-800 space-y-2">
                      <li>Cover page, logo, all quote details, photos</li>
                      <li>Accept button → triggers digital signature + email confirmation</li>
                    </ul>
                  </div>
                  <p className="text-gray-700">
                    Once accepted, both parties get a signed copy, and the status updates automatically.
                  </p>
                </div>
                
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/469c2845-8a5e-44f9-aa92-bf9cdc9b8bd5.png" 
                      alt="Client view of quote showing final pricing and accept/decline buttons"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">✅ All Set!</h3>
                    <p className="text-gray-700 mb-6">
                      Now you're ready to quote, follow up, and close jobs with total clarity in Quickatoo.
                    </p>
                    <Button 
                      size="lg" 
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                      onClick={() => window.open('https://app.quickatoo.com/quotes', '_blank')}
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Manage Your Quotes
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "create-a-quote":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Creating Your Quote in Quickatoo</h1>
              <p className="text-xl text-gray-600 mb-8">
                Set up and send your first professional quote in just a few steps.
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/OXSgXU_kFkY"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Creating Your Quote in Quickatoo Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div className="space-y-12">
              {/* Step 1: Start a New Quote */}
              <div id="start-new-quote" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">1. Start a New Quote</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Go to Quotes in the side menu and click the ➕ button at the bottom-right to create a new quote.
                  </p>
                  <p className="text-lg text-gray-700">
                    Select an existing client, or add a new one if needed.
                  </p>
                  <p className="text-lg text-gray-700">
                    Choose the project type (e.g., Residential Repaint) or add a custom type.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/677daa1b-5751-42f5-b8ee-d0fa037f9a4f.png" 
                        alt="New Quote creation form showing client selection and project type options" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 2: Fill Quote Details */}
              <div id="fill-quote-details" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">2. Fill Quote Details</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    The dashboard shows the quote location, value, total hours, and client details.
                  </p>
                  <p className="text-lg text-gray-700">
                    Edit any information if needed.
                  </p>
                  <p className="text-lg text-gray-700">
                    For interiors, go to the Interior tab → Interior Quote Details.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/02219f82-f97f-4c52-a860-bed96c74b0bb.png" 
                        alt="Quote dashboard showing location, total value, hours, and client details with tabs for Summary, Interior, Exterior, Photos, and Revisions" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 3: Add Rooms */}
              <div id="add-rooms" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">3. Add Rooms</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Click Add New Room, choose the room type (e.g., Bedroom), and enter dimensions (e.g., 3m x 3m x 2.7m).
                  </p>
                  <p className="text-lg text-gray-700">
                    Quickatoo automatically calculates surface areas.
                  </p>
                  <p className="text-lg text-gray-700">
                    Select surfaces (walls, ceilings, skirting boards) and apply undercoat or cornices.
                  </p>
                  <p className="text-lg text-gray-700">
                    For feature walls, add a custom area (e.g., VJ panel wall 2m x 2.7m) and adjust coats if needed.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/2805459f-0ef7-4900-a585-9c150904df1b.png" 
                        alt="Add New Room modal with bedroom selected, dimensions (3x3x2.7m), surface options for walls and ceiling with undercoat settings, and linear surfaces like skirting" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 4: Add Doors & Windows */}
              <div id="add-doors-windows" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">4. Add Doors & Windows</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Include doors and frames separately, with undercoat applied.
                  </p>
                  <p className="text-lg text-gray-700">
                    Add windows by selecting the size, applying the undercoat, and saving the room.
                  </p>
                  <p className="text-lg text-gray-700">
                    Each room appears as a room card with details and updated stats.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/36d19987-a459-4b94-aa91-950ca3b4d254.png" 
                        alt="Add Door modal showing Standard Flush Panel Door selection with undercoat option, quantity setting, and coats configuration" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 5: Measurement Modes */}
              <div id="measurement-modes" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">5. Measurement Modes</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Choose Auto mode for on-site auto calculations or Manual mode for floor-plan-based dimensions.
                  </p>
                  <p className="text-lg text-gray-700">
                    Add additional rooms (e.g., a bathroom of 10m² with doors and frames).
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/8dbbdfa6-5656-4484-9b12-8a54183ec30c.png" 
                        alt="Add New Room modal for bathroom showing Manual measurement mode with area input (10m²) and surface selection options" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 6: Exterior Quote */}
              <div id="exterior-quote" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">6. Exterior Quote</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Switch to the Exterior tab → Exterior Quote Details.
                  </p>
                  <p className="text-lg text-gray-700">
                    Add surfaces by property sides (front, left, right, back) with dimensions and coats.
                  </p>
                  <p className="text-lg text-gray-700">
                    Include extras like gutters (e.g., 10m) and eaves (e.g., 10 x 0.7m).
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/55b988a3-6b32-4872-8385-78b3870327fe.png" 
                        alt="Exterior quote details page showing walls (27m²), soffits (7m²), gutters (10m), doors and windows sections with area and linear surface options" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 7: Review & Finalise */}
              <div id="review-finalise" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">7. Review & Finalise</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    View the project summary — interior and exterior breakdowns, total hours, paint required, and costs.
                  </p>
                  <p className="text-lg text-gray-700">
                    Add extra hours for setup or extra materials (tape, plastic, etc.).
                  </p>
                  <p className="text-lg text-gray-700">
                    If needed, enable home warranty, and Quickatoo will calculate it automatically.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/574bece0-e6df-4697-a5c2-30d24bfe6740.png" 
                        alt="Time and Materials summary showing total hours (35.15), estimated days (5), paint required (70L), interior and exterior breakdown with labour and topcoat costs" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Step 8: Save & Send */}
              <div id="save-send" className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">8. Save & Send</h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    Click Save Quote, then use the Mail button to send it instantly to the client.
                  </p>
                  <p className="text-lg text-gray-700">
                    The quote updates automatically and is ready for review.
                  </p>
                  
                  <Card className="w-full max-w-2xl mx-auto bg-gray-50 rounded-lg border shadow-sm">
                    <CardContent className="p-4">
                      <img 
                        src="/lovable-uploads/cac12605-e75c-4084-972b-f81fa3eb7453.png" 
                        alt="Final quote overview showing project details, client information, client reminders toggle, and time/materials summary ready for sending" 
                        className="w-full rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Completion Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">✓</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-green-800">Your Quote is Ready!</h3>
                  </div>
                </div>
                <p className="text-green-700">
                  You've now created and sent your first professional quote with Quickatoo.
                </p>
              </div>

              {/* Action Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to create your first quote?</h3>
                    <p className="text-gray-600">Start building professional quotes that win more jobs.</p>
                  </div>
                  <Button 
                    onClick={() => setActiveSection("quote-management")}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Quote
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case "terms-conditions":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Quickatoo Guide: Add Your Terms & Conditions</h1>
              <p className="text-xl text-gray-600 mb-8">
                Set it up once. Reuse it forever. Here's how 👇
              </p>
            </div>

            {/* YouTube Video */}
            <div className="mb-8">
              <HeroVideoDialog
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/yFcv9u13Ux4"
                thumbnailSrc={videoThumb}
                thumbnailAlt="Quickatoo Terms & Conditions Tutorial Video"
                className="w-full max-w-4xl mx-auto"
              />
            </div>

            <div id="go-quote-settings">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Go to Quote Settings</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Open the side menu → Click Settings → Select Quote Settings
                </p>
                <p className="text-gray-700">
                  Scroll to the bottom and hit "Add T&C"
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/90a14621-2367-4c83-8f6d-4fe00777edc4.png"
                       alt="Quickatoo dashboard showing navigation to settings"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-new-term">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Add a New Term</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  The "Add New T&C" form will pop up.
                </p>
                <p className="text-gray-700">Pick a Category:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Inclusions (what's included)</li>
                  <li>Exclusions</li>
                  <li>Specifications</li>
                  <li>Paint Application</li>
                </ul>
                <p className="text-gray-700">Then fill in the content box.</p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/08c5f51d-086d-4deb-b0b1-c3e9b9d98be7.png"
                       alt="Add New T&C form with Specifications category selected"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="example-inclusions">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Example: Add Inclusions</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Choose Inclusions → Type something like:
                </p>
                <p className="text-gray-700 font-mono bg-gray-100 p-2 rounded">
                  Use of premium-quality paints (Dulux or equivalent)
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/290ff591-dd88-4e2c-86e6-30483a1a6e09.png"
                       alt="Add New T&C form with Inclusions category selected"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="format-text">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Format Your Text</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Highlight any word and use Bold or Italic if needed.
                </p>
                <p className="text-gray-700">
                  Click Add Term when ready.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/f5047fa6-5d51-4062-b4b6-a66750909fe2.png"
                       alt="Content box with formatted text showing premium-quality paints information"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="confirm-saved">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Confirm It's Saved</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  You'll see a message like:
                </p>
                <p className="text-gray-700 font-semibold">
                  "Term added successfully"
                </p>
                <p className="text-gray-700">
                  Your new term shows in the list.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/67e53e6b-d14d-43a4-a93f-7b7d9a524377.png"
                       alt="Success message and Terms & Conditions list showing the newly added Inclusions term"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="add-spec-optional">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Add a Spec (Optional)</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Click Add T&C again
                </p>
                <p className="text-gray-700">
                  Category → Specification
                </p>
                <p className="text-gray-700">
                  Subcategory → e.g. Preparation
                </p>
                <p className="text-gray-700">
                  Add details like:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Loose paint will be scraped</li>
                  <li>Small holes filled</li>
                  <li>Bare surfaces spot primed</li>
                </ul>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/e28aea53-5dfc-4ce2-93b6-d12393d2c280.png"
                       alt="Specifications category dropdown showing subcategory options including Preparation, Application, Colours, etc."
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="make-default">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Make Them Default</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Tick the terms you want included in every quote
                </p>
                <p className="text-gray-700">
                  Click Save Changes
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                    <img 
                      src="/lovable-uploads/68b721c5-2f90-40bd-a95e-f436fbf3f688.png"
                      alt="Terms & Conditions settings page with Save Changes button visible"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="done">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Done!</h2>
              <div className="space-y-6">
                <p className="text-gray-700">
                  You'll get a confirmation:
                </p>
                <p className="text-gray-700 font-semibold">
                  "All settings updated successfully"
                </p>
                <p className="text-gray-700">
                  Your T&Cs will now appear on all quotes automatically.
                </p>
                <Card className="bg-gray-50">
                  <CardContent className="p-6">
                     <img 
                       src="/lovable-uploads/e4e933d7-7278-46bf-bbb4-fa8e8156754d.png"
                       alt="Success confirmation showing All settings updated successfully message"
                      className="w-full max-w-2xl mx-auto rounded-lg border shadow-sm"
                    />
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-50 border-purple-200 mb-4">
                  <CardContent className="p-6">
                    <p className="text-purple-800">
                      <strong>💡 Pro Tip:</strong> Well-defined terms and conditions protect your business and set clear expectations with clients, helping prevent disputes and misunderstandings.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">🎉 Congratulations!</h3>
                    <p className="text-gray-700 mb-6">
                      Your Terms & Conditions are now set up and will automatically appear on all new quotes. You can always add more terms or modify existing ones anytime.
                    </p>
                    <div className="flex justify-center">
                      <Button 
                        size="lg" 
                        className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                        onClick={() => window.open('https://app.quickatoo.com/settings', '_blank')}
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        Manage Your Terms & Conditions
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-50 border-blue-200 mt-6">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3">📝 Need Template Examples?</h3>
                    <p className="text-blue-800 mb-4">
                      Get professional Terms & Conditions templates that you can copy and customize for your painting business. 
                      Save time with our ready-to-use examples covering inclusions, exclusions, specifications, and more.
                    </p>
                    <div className="flex justify-center">
                      <Button 
                        size="lg"
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3"
                        onClick={() => {
                          // Navigate to templates section
                          setActiveSection('terms-conditions-templates');
                          window.location.hash = 'terms-conditions-templates';
                          
                          // Scroll to top of content area after section renders
                          setTimeout(() => {
                            const contentContainer = document.querySelector('.overflow-y-auto.h-\\[calc\\(100vh-4rem\\)\\]');
                            
                            if (contentContainer) {
                              contentContainer.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                              });
                            } else {
                              window.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                              });
                            }
                          }, 150);
                        }}
                      >
                        <ChevronRight className="w-4 h-4 mr-2" />
                        View T&C Templates
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "terms-conditions-templates":
        return (
          <div className="space-y-8" data-section="terms-conditions-templates">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions Templates</h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional terms and conditions templates that you can copy and customize for your painting business.
              </p>
            </div>

            <div id="template-overview">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Template Overview</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Use these professionally crafted terms and conditions templates to protect your painting business and set clear expectations with clients. 
                  Each template is based on real-world painting contractor requirements and can be easily customized to fit your specific needs.
                </p>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <p className="text-blue-800">
                      <strong>💡 How to Use:</strong> Simply click the copy button next to any template text, then paste it into your Terms & Conditions settings in Quickatoo. 
                      Remember to customize the details to match your specific business practices and local regulations.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div id="inclusions-templates">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Inclusions Templates</h2>
              <div className="space-y-4">
                <p className="text-gray-700 mb-6">
                  Use these examples to clearly define what's included in your painting services:
                </p>
                
                <div className="grid gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Professional Materials</h3>
                        <EnhancedCopyButton textToCopy="Professional-grade materials using recognized premium brands (such as Dulux or equivalent quality)" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Professional-grade materials using recognized premium brands (such as Dulux or equivalent quality)"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Access Equipment</h3>
                        <EnhancedCopyButton textToCopy="Access equipment provided for work up to 2.5 meters in height" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Access equipment provided for work up to 2.5 meters in height"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Surface Preparation</h3>
                        <EnhancedCopyButton textToCopy="Surface preparation including sanding textured areas and applying multiple coats for smooth finish" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Surface preparation including sanding textured areas and applying multiple coats for smooth finish"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Workmanship Warranty</h3>
                        <EnhancedCopyButton textToCopy="Comprehensive workmanship warranty covering 5 years from completion date" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Comprehensive workmanship warranty covering 5 years from completion date"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Specialized Treatments</h3>
                        <EnhancedCopyButton textToCopy="Application of specialized finishes, rust treatment, and mold-resistant additives where required" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Application of specialized finishes, rust treatment, and mold-resistant additives where required"
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div id="exclusions-templates">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Exclusions Templates</h2>
              <div className="space-y-4">
                <p className="text-gray-700 mb-6">
                  Clear exclusions help prevent scope creep and misunderstandings:
                </p>
                
                <div className="grid gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Scope Limitations</h3>
                        <EnhancedCopyButton textToCopy="Work areas not explicitly documented in the project scope and specifications" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Work areas not explicitly documented in the project scope and specifications"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Boundary Structures</h3>
                        <EnhancedCopyButton textToCopy="Perimeter fencing, decorative elements, and texture applications unless specifically mentioned" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Perimeter fencing, decorative elements, and texture applications unless specifically mentioned"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Interior Flooring</h3>
                        <EnhancedCopyButton textToCopy="Interior flooring, garage surfaces, and internal staircase floors" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Interior flooring, garage surfaces, and internal staircase floors"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Client-Supplied Materials</h3>
                        <EnhancedCopyButton textToCopy="Materials, paint, or equipment provided by the client" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Materials, paint, or equipment provided by the client"
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Travel Costs</h3>
                        <EnhancedCopyButton textToCopy="Travel time and accommodation expenses for remote locations (unless specifically included in quote)" />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Travel time and accommodation expenses for remote locations (unless specifically included in quote)"
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div id="specifications-templates">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Specifications Templates</h2>
              <div className="space-y-6">
                <p className="text-gray-700 mb-6">
                  Detailed specifications protect your business and ensure quality standards:
                </p>

                <div className="space-y-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Preparation Standards</h3>
                        <EnhancedCopyButton textToCopy="Deteriorating paint removal through sanding or scraping to sound surface. Minor defects filled using appropriate materials. Bare surfaces spot primed with recommended primer." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Deteriorating paint removal through sanding or scraping to sound surface. Minor defects filled using appropriate materials. Bare surfaces spot primed with recommended primer."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Application Methods</h3>
                        <EnhancedCopyButton textToCopy="Paint application using brush, roller, or spray techniques at contractor's professional discretion to achieve optimal results." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Paint application using brush, roller, or spray techniques at contractor's professional discretion to achieve optimal results."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Coverage Policy</h3>
                        <EnhancedCopyButton textToCopy="Standard coverage includes two coats. Additional coats for challenging colors or surfaces may incur extra charges for labor and materials." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Standard coverage includes two coats. Additional coats for challenging colors or surfaces may incur extra charges for labor and materials."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Client Responsibilities</h3>
                        <EnhancedCopyButton textToCopy="Client to provide clear access to all work areas, move valuable items, and ensure adequate parking for equipment. Landscaping should be trimmed to allow proper access for painting work." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Client to provide clear access to all work areas, move valuable items, and ensure adequate parking for equipment. Landscaping should be trimmed to allow proper access for painting work."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Environmental Cleanup</h3>
                        <EnhancedCopyButton textToCopy="Reasonable effort will be made to clean up painting debris upon completion. Site to be left in tidy condition with all equipment removed." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Reasonable effort will be made to clean up painting debris upon completion. Site to be left in tidy condition with all equipment removed."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Hazardous Materials</h3>
                        <EnhancedCopyButton textToCopy="Pre-existing hazardous coatings, materials or substances such as lead-based paints or asbestos will be subject to additional costs for safe preparation and removal." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Pre-existing hazardous coatings, materials or substances such as lead-based paints or asbestos will be subject to additional costs for safe preparation and removal."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900">Variation Work</h3>
                        <EnhancedCopyButton textToCopy="Any additional work or changes to the original scope will be charged at $90.00 per hour plus materials, or as per separate quotation." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Any additional work or changes to the original scope will be charged at $90.00 per hour plus materials, or as per separate quotation."
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div id="paint-application-templates">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Paint Application Templates</h2>
              <div className="space-y-4">
                <p className="text-gray-700 mb-6">
                  Specific application methods and coating specifications:
                </p>
                
                <div className="grid gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Exterior Low Sheen Acrylic</h3>
                          <EnhancedCopyButton textToCopy="Exterior Low Sheen Acrylic" />
                        </div>
                        <EnhancedCopyButton textToCopy="Spot prime bare areas. Apply 2 coats of premium exterior low sheen acrylic paint. Coverage includes: Walls, Soffits, Downpipes, Trim, and Rendered surfaces." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Spot prime bare areas. Apply 2 coats of premium exterior low sheen acrylic paint. Coverage includes: Walls, Soffits, Downpipes, Trim, and Rendered surfaces."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Exterior Gloss Acrylic</h3>
                          <EnhancedCopyButton textToCopy="Exterior Gloss Acrylic" />
                        </div>
                        <EnhancedCopyButton textToCopy="Spot prime bare areas. Apply 2 coats of premium exterior gloss acrylic paint. Coverage includes: Gutters, Fascia, Handrails, and Window frames." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Spot prime bare areas. Apply 2 coats of premium exterior gloss acrylic paint. Coverage includes: Gutters, Fascia, Handrails, and Window frames."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Interior Washable Low Sheen</h3>
                          <EnhancedCopyButton textToCopy="Interior Washable Low Sheen" />
                        </div>
                        <EnhancedCopyButton textToCopy="Spot prime bare areas. Apply 2 coats of premium interior washable low sheen acrylic paint. Coverage includes: Internal walls and plaster surfaces." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Spot prime bare areas. Apply 2 coats of premium interior washable low sheen acrylic paint. Coverage includes: Internal walls and plaster surfaces."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Gloss Enamel Finish</h3>
                          <EnhancedCopyButton textToCopy="Gloss Enamel Finish" />
                        </div>
                        <EnhancedCopyButton textToCopy="Apply 1 coat primer/undercoat followed by 1 coat premium enamel. Coverage includes: Windows, Doors, Frames, Skirting, and Architraves." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Apply 1 coat primer/undercoat followed by 1 coat premium enamel. Coverage includes: Windows, Doors, Frames, Skirting, and Architraves."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Ceiling Application</h3>
                          <EnhancedCopyButton textToCopy="Ceiling Application" />
                        </div>
                        <EnhancedCopyButton textToCopy="Spot prime bare areas. Apply 2 coats of premium ceiling flat acrylic paint to all internal ceiling surfaces." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "Spot prime bare areas. Apply 2 coats of premium ceiling flat acrylic paint to all internal ceiling surfaces."
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">Timber Treatments</h3>
                          <EnhancedCopyButton textToCopy="Timber Treatments" />
                        </div>
                        <EnhancedCopyButton textToCopy="The quoted price includes up to two (2) coatings for all timber elements. These may be clear finish, stain, oil, or sealer applications as specified." />
                      </div>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        "The quoted price includes up to two (2) coatings for all timber elements. These may be clear finish, stain, oil, or sealer applications as specified."
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div id="get-help">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Need Custom Help?</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  Need help customizing these templates for your specific business needs? Our team can help you create 
                  professional terms and conditions tailored to your painting business.
                </p>
                
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">🚀 Professional Setup Service</h3>
                    <p className="text-gray-700 mb-6">
                      We can help you set up standard Terms & Conditions or implement your existing T&C. 
                      Get your terms properly configured and integrated with your Quickatoo system.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button 
                        size="lg" 
                        className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                        onClick={() => window.open('mailto:info@quickatoo.com?subject=T%26C%20template%20support', '_blank')}
                      >
                        <HeadphonesIcon className="w-5 h-5 mr-2" />
                        Contact Our Team
                      </Button>
                      <Button 
                        size="lg" 
                        variant="outline"
                        className="border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-3"
                        onClick={() => window.open('https://app.quickatoo.com/settings', '_blank')}
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        Setup Your Terms Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "faq":
        const faqData = [
          {
            id: "faq-1",
            question: "How does Quickatoo calculate material costs?",
            answer: "Quickatoo uses an advanced estimating algorithm that factors in surface area, primer and topcoat coverage, spread rates, labor efficiency, and additional materials like sundries. You can use industry-standard pricing or customize it based on your preferred suppliers and job-specific requirements."
          },
          {
            id: "faq-2",
            question: "Can I customize my quotes with my company branding?",
            answer: "Absolutely! Quickatoo lets you add your company logo, business name, contact details, and personalized terms and conditions to your quotes, ensuring a professional and consistent brand image for your clients."
          },
          {
            id: "faq-3",
            question: "Can I integrate Quickatoo with my accounting software?",
            answer: "Yes! We are currently developing integrations for Xero and MYOB to streamline your invoicing and financial management. Stay tuned for updates as we expand compatibility with accounting platforms."
          },
          {
            id: "faq-4",
            question: "What types of painting jobs does Quickatoo support?",
            answer: "Quickatoo is built to handle a wide range of painting projects, including: Residential (interior & exterior), Commercial & industrial projects, Special finishes (Venetian plaster, decorative coatings), Decking and protective coatings, Maintenance and repaints."
          },
          {
            id: "faq-5",
            question: "How accurate are the measurements taken through Quickatoo?",
            answer: "Quickatoo provides highly accurate measurements by using industry-standard formulas for surface areas, paint coverage, and application rates. Users can input custom dimensions and adjust variables to ensure real-world precision."
          },
          {
            id: "faq-6",
            question: "Is there a limit to how many quotes I can create?",
            answer: "We have plans that fit your needs, so you only pay for what you use—no wasted services."
          },
          {
            id: "faq-7",
            question: "Can multiple team members use the same account?",
            answer: "Yes! Quickatoo supports team collaboration, allowing multiple users to access the same account with role-based permissions. Whether you're an estimator, project manager, or painter, everyone stays connected and aligned."
          },
          {
            id: "faq-8",
            question: "How secure is my customer data in Quickatoo?",
            answer: "We take data security seriously. Quickatoo uses end-to-end encryption, secure cloud storage, and regular backups to keep your customer data protected and confidential."
          }
        ];

        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
              <p className="text-xl text-gray-600 mb-8">
                Everything you need to know about Quickatoo. Can't find the answer you're looking for? Feel free to contact our support team.
              </p>
            </div>

            <div className="space-y-6">
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((item) => (
                  <AccordionItem key={item.id} value={item.id}>
                    <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                      <div className="font-medium text-left sm:py-1 lg:py-2 lg:text-lg">
                        {item.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="sm:mb-1 lg:mb-2">
                      <div className="text-gray-600 lg:text-lg">
                        {item.answer}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="flex flex-col items-center rounded-lg bg-gray-50 p-6 text-center lg:p-8 mt-12">
                <div className="relative mb-6">
                  <Avatar className="absolute mb-4 size-16 origin-bottom -translate-x-[60%] scale-[80%] border">
                    <AvatarImage src="https://shadcnblocks.com/images/block/avatar-2.webp" />
                    <AvatarFallback>SU</AvatarFallback>
                  </Avatar>
                  <Avatar className="absolute mb-4 size-16 origin-bottom translate-x-[60%] scale-[80%] border">
                    <AvatarImage src="https://shadcnblocks.com/images/block/avatar-3.webp" />
                    <AvatarFallback>SU</AvatarFallback>
                  </Avatar>
                  <Avatar className="mb-4 size-16 border">
                    <AvatarImage src="https://shadcnblocks.com/images/block/avatar-1.webp" />
                    <AvatarFallback>SU</AvatarFallback>
                  </Avatar>
                </div>
                <h3 className="mb-2 font-semibold lg:text-lg">
                  Still have questions?
                </h3>
                <p className="mb-8 text-gray-600 lg:text-lg">
                  Can't find the answer you're looking for? Our support team is here to help with any technical questions or concerns.
                </p>
                <Button asChild>
                  <a href="mailto:info@quickatoo.com" target="_blank">
                    Contact Support
                  </a>
                </Button>
              </div>
            </div>
          </div>
        );

      case "contact":
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Support</h1>
              <p className="text-xl text-gray-600 mb-8">
                Our dedicated support team is here to help you with any questions or concerns. Get in touch with us for personalized assistance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Mail className="w-6 h-6 text-purple-600 mr-3" />
                    <h3 className="text-lg font-semibold">Email Support</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Get help with technical questions, account issues, or general inquiries.
                  </p>
                  <Button asChild className="w-full">
                    <a href="mailto:info@quickatoo.com" target="_blank">
                      Email us at info@quickatoo.com
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <MessageCircle className="w-6 h-6 text-purple-600 mr-3" />
                    <h3 className="text-lg font-semibold">Live Chat</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Chat with our support team in real-time through the app for immediate assistance. (Coming Soon)
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <a href="https://app.quickatoo.com" target="_blank">
                      Open Quickatoo Chat
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Immediate Help?</h3>
              <p className="text-gray-600 mb-4">
                Check our documentation for quick answers to common questions.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setActiveSection('faq')}
              >
                View FAQ
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Documentation</h1>
              <p className="text-xl text-gray-600">
                Select a topic from the sidebar to get started.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex-1 max-w-4xl mx-auto px-4 md:px-8 py-8">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink 
              href="#introduction" 
              onClick={(e) => {
                e.preventDefault();
                setActiveSection("introduction");
              }}
            >
              Documentation
            </BreadcrumbLink>
          </BreadcrumbItem>
          {sectionInfo.breadcrumbs && sectionInfo.breadcrumbs.map((breadcrumb, index) => (
            <div key={index} className="flex items-center">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink 
                  href={breadcrumb.link}
                  onClick={(e) => {
                    e.preventDefault();
                    const sectionId = breadcrumb.link.replace('#', '');
                    setActiveSection(sectionId);
                  }}
                >
                  {breadcrumb.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </div>
          ))}
          {sectionInfo.title !== "Documentation" && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{sectionInfo.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
      
      {renderContent()}
      
      <FloatingTableOfContents activeSection={section} />
    </div>
  );
};

const Documentation = () => {
  const [activeSection, setActiveSection] = useState("introduction");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Custom handler to change section and scroll to top
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    // Scroll main content to top
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  };

  // Define valid documentation sections (not in-page anchors)
  const validSections = [
    "introduction", "quick-start", "add-new-surface", "add-new-tasks", 
    "business-admin", "sync-xero", "sync-myob", "sync-quickbooks", 
    "sync-sage", "troubleshooting", "faq", "contact-support"
  ];

  // Handle URL hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the '#'
      if (hash && validSections.includes(hash)) {
        handleSectionChange(hash);
      }
    };

    // Set initial section from URL hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Learn Quickatoo — Guides for Painting Contractors"
        description="Guides, video tutorials and FAQs for Quickatoo: set up quote templates, manage clients and substrates, and send professional painting quotes."
        path="/learn"
        type="article"
        jsonLd={learnJsonLd}
      />
      <Navbar />

      
      {/* Mobile Header */}
      <div className="md:hidden fixed top-16 left-0 right-0 bg-white border-b border-gray-200 px-4 py-3 z-30">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1 ml-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sm">Documentation</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
      </div>
      
      <div className="flex pt-16 md:pt-16">
        <DocumentationSidebar 
          activeSection={activeSection} 
          onSectionChange={handleSectionChange}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        
        {/* Main Content - Scrollable */}
        <div ref={mainContentRef} className="flex-1 min-w-0 pt-16 md:pt-0 overflow-y-auto h-[calc(100vh-4rem)]">
          <ContentSection section={activeSection} setActiveSection={handleSectionChange} />
        </div>
        
        <TableOfContents activeSection={activeSection} />
      </div>
      
      {/* Floating Table of Contents for Mobile */}
      <FloatingTableOfContents activeSection={activeSection} />
    </div>
  );
};

export default Documentation;
